
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {MdEdit, MdRateReview, MdVisibility} from "react-icons/md";
import TableColumn from "../../TableColumn/TableColumn";
import SideBar from "../../SideBar/SideBar";
import TopNav from "../../TopNav/TopNav";
import api from "../../../services/api";

// Capsule shown in the Receipts column. Clicking opens the lightbox below.
function ReceiptCapsule({ urls, onView }) {
    if (!urls || urls.length === 0) {
        return (
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                color: "rgba(255, 255, 255, 0.45)",
                fontSize: 12,
                fontWeight: 500,
            }}>
                None
            </span>
        );
    }
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onView(urls); }}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 999,
                background: "rgba(114, 41, 245, 0.18)",
                border: "1px solid rgba(114, 41, 245, 0.4)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
            }}
            title={`View ${urls.length} receipt${urls.length === 1 ? "" : "s"}`}
        >
            <MdVisibility size={14} />
            {urls.length} {urls.length === 1 ? "receipt" : "receipts"}
        </button>
    );
}

function WithdrawalRequests() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(
        Number(localStorage.getItem("ListCurrentPageWithdrawals")) || 1
    );
    const records = 10;
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("null");
    const [receiptViewer, setReceiptViewer] = useState(null); // null | string[]
    const getStatusColor = (status) => {
        return status === 'approved'
            ? '#28a745' // green
            : status === 'rejected'
                ? '#dc3545' // red
                : status === 'pending'
                    ? '#ffc107' // yellow
                    : status === 'processed'
                        ? '#17a2b8' // teal/cyan
                        : status === 'processing'
                            ? '#6f42c1' // purple
                            : '#FFFFFF'; // fallback
    };


    useEffect(() => {
        const fetchRequests = async (page) => {
            try {
                const response = await api.get("venue-owners/fetch/withdrawal-requests", {
                    params: {
                        records,
                        page,
                        search: searchTerm,
                        sort_column: sortBy,
                        sort: sortDirection,
                    },
                });


                const formatted = response.data.data.map((req) => ({
                    ID: req._id,
                    "Transaction ID": req.transaction_id || "N/A",
                    Amount: `$${(Number(req.requested_amount) / 100).toFixed(2)}`,
                    Status: (
                        <span
                            style={{
                                color: getStatusColor(req.status),
                                fontWeight: "bold",
                                textTransform: "capitalize"
                            }}
                        >
            {req.status}
        </span>
                    ),
                    Receipts: (
                        <ReceiptCapsule
                            urls={req.receipt_urls || []}
                            onView={(urls) => setReceiptViewer(urls)}
                        />
                    ),
                    "Requested At": new Date(req.requested_at).toLocaleString('en-US', { timeZone: 'America/New_York' }),
                    Owner: req.owner_id?.name || "N/A",
                    Email: req.owner_id?.email || "N/A",
                    Bank: req.account_info?.bank_name || "N/A",
                }));


                setWithdrawalRequests(formatted);
                setTotalPages(response.data.total);
            } catch (err) {
                console.error("Failed to fetch withdrawal requests:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests(currentPage);
    }, [currentPage, searchTerm, sortBy, sortDirection]);

    const fetchAllData = async () => {
        try {
            const response = await api.get("venue-owners/fetch/withdrawal-requests", {
                params: { records: 999999 },
            });

            return response.data.data.map((req) => ({
                ID: req._id,
                "Transaction ID": req.transaction_id || "N/A",
                Amount: `$${(Number(req.requested_amount)/100).toFixed(2)}`,
                Status: (
                    <span
                        style={{
                            color: getStatusColor(req.status),
                            fontWeight: "bold",
                            textTransform: "capitalize"
                        }}
                    >
            {req.status}
        </span>
                ),
                Receipts: (
                    <ReceiptCapsule
                        urls={req.receipt_urls || []}
                        onView={(urls) => setReceiptViewer(urls)}
                    />
                ),
                "Requested At": new Date(req.requested_at).toLocaleString('en-US', { timeZone: 'America/New_York' }),
                Owner: req.owner_id?.name || "N/A",
                Email: req.owner_id?.email || "N/A",
                Bank: req.account_info?.bank_name || "N/A",
            }));
        } catch (err) {
            console.error("Error fetching all withdrawal data:", err);
            return [];
        }
    };

    const handleChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        localStorage.setItem("ListCurrentPageWithdrawals", page);
    };

    const sortColumnsMapping = {
        "Transaction ID": "transaction_id",
        Amount: "requested_amount",
        Status: "status",
        Owner: "owner_id.name",
        Email: "owner_id.email",
        "Requested At": "requested_at",
    };

    const handleSort = (columnName) => {
        const backendColumnName = sortColumnsMapping[columnName];
        if (sortBy === backendColumnName) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(backendColumnName);
            setSortDirection("asc");
        }
    };

    const actions = [
        {
            label: <MdVisibility size={26} color="#4F30EA" />,
            tooltip: "View details",
            onClick: (rowData) => {
                console.log(rowData)
                navigate(`/withdrawal-request-details/${rowData.ID}`);

            },
        },
        // {
        //     label: <MdRateReview size={26} color="#4F30EA" />,
        //     tooltip: "Review / Update Status",
        //     onClick: (rowData) => {
        //         setSelectedRequest(rowData);
        //         setShowReviewModal(true);
        //     },
        // },
    ];


    return (
        <main>
            <TopNav />
            <div className="main-container">
                <SideBar />
                <article
                    style={{ width: "100%", display: "flex", justifyContent: "center", overflow: "hidden" }}
                >
                    <section className="venues">
                        <TableColumn
                            title="Withdrawal Requests"
                            columns={[
                                "Transaction ID",
                                "Amount",
                                "Status",
                                "Receipts",
                                "Requested At",
                                "Owner",
                                "Email",
                                "Bank",
                            ]}
                            data={withdrawalRequests}
                            sortableColumns={[
                                "Transaction ID",
                                "Amount",
                                "Status",
                                "Owner",
                                "Email",
                                "Requested At",
                            ]}
                            hasExport={true}
                            hasSearch={true}
                            totalPages={totalPages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            records={records}
                            actions={actions}
                            inputSearchProps={{ value: searchTerm, onChange: handleChange }}
                            fetchAllData={fetchAllData}
                            onSort={handleSort}
                        />
                    </section>
                </article>
            </div>

            {receiptViewer && (
                <div
                    onClick={() => setReceiptViewer(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.85)",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "min(96vw, 1200px)",
                            maxHeight: "90vh",
                            overflow: "auto",
                            background: "#0f0f12",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: 16,
                            padding: 20,
                        }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 16,
                            color: "#fff",
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                                Receipts ({receiptViewer.length})
                            </h3>
                            <button
                                onClick={() => setReceiptViewer(null)}
                                style={{
                                    background: "rgba(255, 255, 255, 0.05)",
                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                    borderRadius: 8,
                                    padding: "6px 12px",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontSize: 13,
                                }}
                            >
                                Close
                            </button>
                        </div>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: 12,
                        }}>
                            {receiptViewer.map((url, i) => (
                                <a
                                    key={i}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "block",
                                        borderRadius: 10,
                                        overflow: "hidden",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                    }}
                                    title="Open full size in new tab"
                                >
                                    <img
                                        src={url}
                                        alt={`Receipt ${i + 1}`}
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            height: "auto",
                                        }}
                                    />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </main>
    );





}



export default WithdrawalRequests;
